import React, { createContext, useContext, useEffect, ReactNode, useState } from "react";
import { ICity, IReferral, IBanner, IUserData, ApiState } from "interfaces/home";
import { useApiCall } from "hooks/useApiCall";
import { getCities, getReferralsList, getHomeBanners, getUserData } from "api/home";
import { isAPISuccessful, getAPIErrorMessage } from "utils/apiHelpers";

export interface IHomeDataContext {
    isLoading: boolean;
    cities: ApiState<ICity[]>;
    referrals: ApiState<IReferral[]>;
    banners: ApiState<IBanner[]>;
    userData: ApiState<IUserData>;
    retryCities: () => void;
    retryReferrals: () => void;
    retryBanners: () => void;
    retryUserData: () => void;
}

const HomeDataContext = createContext<IHomeDataContext | null>(null);

export const HomeDataProvider = ({ children }: { children: ReactNode }) => {
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const { apiState: cities, fetch: fetchCities } = useApiCall(async () => {
        const response = await getCities();
        if (!isAPISuccessful(response)) {
            throw new Error(getAPIErrorMessage(response) || "Something went wrong");
        }
        return response.data.cities;
    });
    const { apiState: referrals, fetch: fetchReferrals } = useApiCall(async () => {
        const response = await getReferralsList();
        if (!isAPISuccessful(response)) {
            throw new Error(getAPIErrorMessage(response) || "Something went wrong");
        }
        return response.data.referrals;
    });
    const { apiState: banners, fetch: fetchBanners } = useApiCall(async () => {
        const response = await getHomeBanners();
        if (!isAPISuccessful(response)) {
            throw new Error(getAPIErrorMessage(response) || "Something went wrong");
        }
        return response.data.banners;
    });
    const { apiState: userData, fetch: fetchUserData } = useApiCall(async () => {
        const response = await getUserData();
        if (!isAPISuccessful(response)) {
            throw new Error(getAPIErrorMessage(response) || "Something went wrong");
        }
        return response.data;
    });

    // Fetch all APIs on mount - only happens once
    useEffect(() => {
        Promise.allSettled([fetchCities(), fetchReferrals(), fetchBanners(), fetchUserData()]).finally(() => {
            setInitialLoadComplete(true);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <HomeDataContext.Provider
            value={{
                isLoading: !initialLoadComplete,
                cities,
                referrals,
                banners,
                userData,
                retryCities: fetchCities,
                retryReferrals: fetchReferrals,
                retryBanners: fetchBanners,
                retryUserData: fetchUserData,
            }}
        >
            {children}
        </HomeDataContext.Provider>
    );
};

export const useHomeData = () => {
    const context = useContext(HomeDataContext);
    if (!context) {
        throw new Error("useHomeData must be used within HomeDataProvider");
    }
    return context;
};
